import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import axios from 'utils/axios';
import { dispatch } from 'store';
import { openSnackbar } from 'store/reducers/snackbar';

/**
 * Custom hook to fetch roles from the API.
 * Utilizes the useQuery hook from react-query to perform
 * the data fetching and caching.
 *
 * @returns {Object} - Returns a query object containing
 * data, status, error, and other properties related to the
 * query's execution state.
 */
export const useGetRoles = () => {
  return useQuery({
    queryKey: ['roles'],
    queryFn: async () => {
      const { data } = await axios.get('/role/all');
      return data;
    }
  });
};

/**
 * Custom hook to fetch a role by id from the API.
 * Utilizes the useQuery hook from react-query to
 * perform the data fetching and caching.
 *
 * @param {string} id - The id of the role to fetch.
 *
 * @returns {Object} - Returns a query object containing
 * data, status, error, and other properties related to the
 * query's execution state.
 */
export const useGetRole = (id) => {
  return useQuery({
    queryKey: ['role', id],
    queryFn: async () => {
      const { data } = await axios.get(`/role/${id}`);
      return data;
    }
  });
};

/**
 * Custom hook to add a new role to the API.
 * Utilizes the useMutation hook from react-query to
 * perform the mutation and cache invalidation.
 *
 * @returns {Object} - Returns a mutation object containing
 * mutate, mutateAsync, reset, and other properties related to the
 * mutation's execution state.
 */
export const useAddRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      console.log('🚀 ~ mutationFn: ~ data:', data);
      const response = await axios.post('/role', data).then((res) => res);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['roles']);
      dispatch(
        openSnackbar({
          message: 'Role added successfully',
          open: true,
          variant: 'alert',
          alert: {
            color: 'success'
          },
          close: true
        })
      );
    },
    onError: () => {
      dispatch(
        openSnackbar({
          message: 'Role add failed',
          open: true,
          variant: 'alert',
          alert: {
            color: 'error'
          },
          close: true
        })
      );
    }
  });
};

/**
 * Custom hook to update a role in the API.
 * Utilizes the useMutation hook from react-query to
 * perform the mutation and cache invalidation.
 *
 * @returns {Object} - Returns a mutation object containing
 * methods and properties to manage the mutation state.
 */
export const useUpdateRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await axios.put(`/role/${id}`, data).then((res) => res);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['roles']);
      dispatch(
        openSnackbar({
          message: 'Role updated successfully',
          open: true,
          variant: 'alert',
          alert: {
            color: 'success'
          },
          close: true
        })
      );
    },
    onError: () => {
      dispatch(
        openSnackbar({
          message: 'Role update failed',
          open: true,
          variant: 'alert',
          alert: {
            color: 'error'
          },
          close: true
        })
      );
    }
  });
};

/**
 * Custom hook to delete a role from the API.
 * Utilizes the useMutation hook from react-query to
 * perform the data deletion and cache invalidation.
 *
 * @returns {Object} - Returns a mutation object containing
 * methods and properties to manage the mutation state.
 */
export const useDeleteRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    // Function to perform the delete request
    mutationFn: async ({ id }) => {
      const response = await axios.delete(`/role/${id}`).then((res) => res);
      return response;
    },
    // Invalidate the 'roles' query on success to refresh data
    onSuccess: () => {
      queryClient.invalidateQueries(['roles']);
      dispatch(
        openSnackbar({
          message: 'Role deleted successfully',
          open: true,
          variant: 'alert',
          alert: {
            color: 'success'
          },
          close: true
        })
      );
    },
    // Show error snackbar on failure
    onError: () => {
      dispatch(
        openSnackbar({
          message: 'Role deletion failed',
          open: true,
          variant: 'alert',
          alert: {
            color: 'error'
          },
          close: true
        })
      );
    }
  });
};

export const useGetPermission = (id) => {
  return useQuery({
    queryKey: ['permissions', id],
    queryFn: async () => {
      const { data } = await axios.get(`/permission/${id}`);
      return data;
    },
    enabled: !!id
  });
};

export const useAddPermission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const response = await axios.post('/permission', data).then((res) => res);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['permissions']);
      dispatch(
        openSnackbar({
          message: 'Permission added successfully',
          open: true,
          variant: 'alert',
          alert: {
            color: 'success'
          },
          close: true
        })
      );
    },
    onError: () => {
      dispatch(
        openSnackbar({
          message: 'Permission add failed',
          open: true,
          variant: 'alert',
          alert: {
            color: 'error'
          },
          close: true
        })
      );
    }
  });
};

export const useUpdatePermission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await axios.put(`/permission/${id}`, data).then((res) => res);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['permissions']);
      dispatch(
        openSnackbar({
          message: 'Permission updated successfully',
          open: true,
          variant: 'alert',
          alert: {
            color: 'success'
          },
          close: true
        })
      );
    },
    onError: () => {
      dispatch(
        openSnackbar({
          message: 'Permission update failed',
          open: true,
          variant: 'alert',
          alert: {
            color: 'error'
          },
          close: true
        })
      );
    }
  });
};

export const useDeletePermission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    // Function to perform the delete request
    mutationFn: async (id) => {
      const response = await axios.delete(`/permission/${id}`).then((res) => res);
      return response;
    },
    // Invalidate the 'roles' query on success to refresh data
    onSuccess: () => {
      queryClient.invalidateQueries(['permissions']);
      dispatch(
        openSnackbar({
          message: 'Permission deleted successfully',
          open: true,
          variant: 'alert',
          alert: {
            color: 'success'
          },
          close: true
        })
      );
    },
    // Show error snackbar on failure
    onError: () => {
      dispatch(
        openSnackbar({
          message: 'Permission deletion failed',
          open: true,
          variant: 'alert',
          alert: {
            color: 'error'
          },
          close: true
        })
      );
    }
  });
};
